<section class="ui-data-table"
         [class.ui-data-table-detail]="variant === 'detail'"
         [attr.data-table-id]="tableId">
    <div class="ui-data-table-toolbar" *ngIf="showToolbar">
        <div class="ui-data-table-status" aria-live="polite">
            <span *ngIf="!loading && !error">
                {{ pageStart }}-{{ pageEnd }} of {{ resolvedTotal }}
            </span>
        </div>

        <div class="ui-data-table-controls">
            <div class="ui-data-table-column-menu" *ngIf="hideableColumns.length > 0">
                <button type="button"
                        class="btn btn-sm btn-outline-secondary"
                        (click)="columnMenuOpen = !columnMenuOpen"
                        [attr.aria-expanded]="columnMenuOpen"
                        aria-label="Choose visible columns">
                    <i class="fa fa-columns" aria-hidden="true"></i>
                    Columns
                </button>
                <div class="ui-data-table-column-panel" *ngIf="columnMenuOpen">
                    <label *ngFor="let column of hideableColumns; trackBy: trackByColumn">
                        <input type="checkbox"
                               [checked]="isColumnVisible(column)"
                               (change)="setColumnVisible(column, $any($event.target).checked)">
                        <span>{{ column.header }}</span>
                    </label>
                </div>
            </div>
        </div>
    </div>

    <div class="ui-data-table-shell">
        <table class="ui-data-table-table" [attr.data-testid]="tableId">
            <caption *ngIf="caption" class="visually-hidden">{{ caption }}</caption>
            <thead>
                <tr>
                    <th *ngIf="hasExpansion" scope="col" class="ui-data-table-expander-header">
                        <span class="visually-hidden">{{ expanderHeaderLabel }}</span>
                    </th>
                    <th *ngFor="let column of visibleColumns; trackBy: trackByColumn"
                        scope="col"
                        [style.width]="column.width || null"
                        [class.ui-data-table-cell-right]="column.align === 'right'"
                        [class.ui-data-table-cell-center]="column.align === 'center'"
                        [ngClass]="headerClass(column)">
                        <div class="ui-data-table-header-cell">
                            <button *ngIf="column.sortable; else plainHeader"
                                    type="button"
                                    class="ui-data-table-sort"
                                    (click)="toggleSort(column)"
                                    [attr.aria-label]="sortLabel(column) + ' ' + column.header">
                                <span>{{ column.header }}</span>
                                <i class="fa" [ngClass]="sortIcon(column)" aria-hidden="true"></i>
                            </button>
                            <ng-template #plainHeader>
                                <span>{{ column.header }}</span>
                            </ng-template>
                        </div>

                        <div class="ui-data-table-filter" *ngIf="isFilterable(column)">
                            <select *ngIf="filterOptionsFor(column).length > 0; else textFilter"
                                    class="form-select form-select-sm"
                                    [ngModel]="filterValue(column.id)"
                                    (ngModelChange)="setFilter(column, $event)"
                                    [attr.aria-label]="'Filter ' + column.header">
                                <option value="">All</option>
                                <option *ngFor="let option of filterOptionsFor(column)" [ngValue]="option.value">
                                    {{ option.label }}
                                </option>
                            </select>
                            <ng-template #textFilter>
                                <input type="search"
                                       class="form-control form-control-sm"
                                       [ngModel]="filterValue(column.id)"
                                       (ngModelChange)="setFilter(column, $event)"
                                       [placeholder]="'Filter ' + column.header"
                                       [attr.aria-label]="'Filter ' + column.header">
                            </ng-template>
                        </div>
                    </th>
                    <th *ngIf="hasActions" scope="col" class="ui-data-table-actions-header">
                        Actions
                    </th>
                </tr>
            </thead>
            <tbody>
                <ng-container *ngFor="let row of pagedRows; let rowIndex = index; trackBy: trackByRow">
                    <tr [ngClass]="rowClassFor(row)"
                        [uiTableRowAttrs]="rowAttrsFor(row)"
                        [attr.data-row-key]="hasExpansion ? rowKeyFor(row, rowIndex) : null">
                        <td *ngIf="hasExpansion" class="ui-data-table-expander-cell">
                            <button *ngIf="canExpand(row)"
                                    type="button"
                                    class="ui-data-table-expander"
                                    [attr.data-testid]="expanderTestId || null"
                                    [attr.aria-expanded]="isExpanded(row, rowIndex)"
                                    [attr.aria-controls]="expandedRowId(row, rowIndex)"
                                    (click)="toggleExpanded(row, $event)">
                                <i class="fa"
                                   [ngClass]="isExpanded(row, rowIndex) ? 'fa-chevron-down' : 'fa-chevron-right'"
                                   aria-hidden="true"></i>
                                <span class="visually-hidden">{{ isExpanded(row, rowIndex) ? 'Collapse' : 'Expand' }}</span>
                            </button>
                        </td>
                        <td *ngFor="let column of visibleColumns; trackBy: trackByColumn"
                            [class.ui-data-table-cell-right]="column.align === 'right'"
                            [class.ui-data-table-cell-center]="column.align === 'center'"
                            [ngClass]="cellClass(column, row)"
                            [attr.data-column-id]="column.id">
                            <ng-container *ngIf="customCellTemplate(column.id) as slot; else defaultCell">
                                <ng-container *ngTemplateOutlet="slot.template; context: { $implicit: row, row: row, column: column, value: valueFor(row, column) }"></ng-container>
                            </ng-container>
                            <ng-template #defaultCell>{{ formatValue(valueFor(row, column)) }}</ng-template>
                        </td>
                        <td *ngIf="hasActions" class="ui-data-table-actions-cell">
                            <ng-container *ngIf="actionsSlot">
                                <ng-container *ngTemplateOutlet="actionsSlot.template; context: { $implicit: row, row: row }"></ng-container>
                            </ng-container>
                            <button *ngFor="let action of rowActions"
                                    type="button"
                                    [class]="actionClass(action)"
                                    [hidden]="actionHidden(action, row)"
                                    [disabled]="actionDisabled(action, row)"
                                    (click)="emitRowAction(action, row, $event)">
                                <i *ngIf="action.icon" class="fa" [ngClass]="action.icon" aria-hidden="true"></i>
                                {{ action.label }}
                            </button>
                        </td>
                    </tr>
                    <tr *ngIf="hasExpansion"
                        class="ui-data-table-expanded-row"
                        [id]="expandedRowId(row, rowIndex)"
                        [hidden]="!isExpanded(row, rowIndex)">
                        <td [attr.colspan]="expandedColspan">
                            <ng-container *ngIf="expandedSlot">
                                <ng-container *ngTemplateOutlet="expandedSlot.template; context: { $implicit: row, row: row }"></ng-container>
                            </ng-container>
                        </td>
                    </tr>
                </ng-container>
            </tbody>
        </table>

        <div class="ui-data-table-state" *ngIf="loading">
            <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
            <span>{{ loadingMessage }}</span>
        </div>

        <div class="ui-data-table-state ui-data-table-state-error" *ngIf="!loading && error">
            <i class="fa fa-triangle-exclamation" aria-hidden="true"></i>
            <span>{{ error || errorMessage }}</span>
        </div>

        <div class="ui-data-table-state" *ngIf="showEmptyState">
            <i class="fa fa-inbox" aria-hidden="true"></i>
            <span>{{ emptyMessage }}</span>
        </div>
    </div>

    <div class="ui-data-table-pagination" *ngIf="pagination">
        <label>
            Rows
            <select class="form-select form-select-sm"
                    [ngModel]="currentPageSize"
                    (ngModelChange)="setPageSize($event)"
                    aria-label="Rows per page">
                <option *ngFor="let option of pageSizeOptions" [ngValue]="option">{{ option }}</option>
            </select>
        </label>

        <div class="ui-data-table-page-buttons">
            <button type="button"
                    class="btn btn-sm btn-outline-secondary"
                    (click)="previousPage()"
                    [disabled]="currentPageIndex === 0">
                <i class="fa fa-chevron-left" aria-hidden="true"></i>
                Previous
            </button>
            <span>Page {{ currentPageIndex + 1 }} of {{ pageCount }}</span>
            <button type="button"
                    class="btn btn-sm btn-outline-secondary"
                    (click)="nextPage()"
                    [disabled]="currentPageIndex >= pageCount - 1">
                Next
                <i class="fa fa-chevron-right" aria-hidden="true"></i>
            </button>
        </div>
    </div>
</section>
